import { useState, type ReactNode } from 'react';
import { Card } from '@alascorporation/ui';

export type EntityDetailNavStatus = 'complete' | 'partial' | 'empty';

export interface EntityDetailNavItem<K extends string = string> {
  key: K;
  label: string;
  /** Create-mode: not yet navigable until the entity is first saved. */
  locked?: boolean;
  /** Optional per-item completion indicator — only meaningful to callers with a completion concept (e.g. student profile). */
  status?: EntityDetailNavStatus;
  /** Optional icon shown before the label (matches the reference design). */
  icon?: ReactNode;
}

export interface EntityDetailNavGroup<K extends string = string> {
  /** Uppercase group header, e.g. "GENERAL". Not interactive. */
  label: string;
  items: EntityDetailNavItem<K>[];
}

interface EntityDetailNavProps<K extends string> {
  groups: EntityDetailNavGroup<K>[];
  activeKey: K;
  onChange: (key: K) => void;
  /** Optional read-only contextual tip rendered below the section list (desktop only). */
  tip?: ReactNode;
}

function LockIcon() {
  return (
    <svg className="size-3.5 shrink-0" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
      <path
        fillRule="evenodd"
        d="M10 1a4 4 0 00-4 4v2H5a2 2 0 00-2 2v7a2 2 0 002 2h10a2 2 0 002-2V9a2 2 0 00-2-2h-1V5a4 4 0 00-4-4zm2 6V5a2 2 0 10-4 0v2h4z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function MenuIcon() {
  return (
    <svg className="size-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
      <path fillRule="evenodd" d="M3 5h14v1.5H3V5zm0 4.25h14v1.5H3v-1.5zM3 13.5h14V15H3v-1.5z" clipRule="evenodd" />
    </svg>
  );
}

const STATUS_DOT_CLASS: Record<EntityDetailNavStatus, string> = {
  complete: 'bg-success-500',
  partial: 'bg-warning-500',
  empty: 'bg-gray-300 dark:bg-gray-600',
};

function NavList<K extends string>({
  groups,
  activeKey,
  onSelect,
}: {
  groups: EntityDetailNavGroup<K>[];
  activeKey: K;
  onSelect: (key: K) => void;
}) {
  return (
    <div role="tablist" aria-label="Secciones" className="space-y-3">
      {groups.map((group) => (
        <div key={group.label}>
          <p className="px-3 pb-1 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-500">
            {group.label}
          </p>
          <div className="space-y-0.5">
            {group.items.map((item) => {
              const active = item.key === activeKey;
              return (
                <button
                  key={item.key}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  disabled={item.locked}
                  title={item.locked ? 'Disponible después de guardar' : undefined}
                  aria-label={item.locked ? `${item.label} — disponible después de guardar` : undefined}
                  onClick={() => !item.locked && onSelect(item.key)}
                  className={`flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-left text-sm font-medium transition-colors ${
                    item.locked
                      ? 'cursor-not-allowed text-gray-300 dark:text-gray-600'
                      : active
                        ? 'bg-brand-50 text-brand-600 dark:bg-brand-500/10 dark:text-brand-300'
                        : 'text-gray-600 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-white/[0.04]'
                  }`}
                >
                  {item.locked ? (
                    <LockIcon />
                  ) : item.icon ? (
                    <span className="flex size-4 shrink-0 items-center justify-center [&>svg]:size-4">{item.icon}</span>
                  ) : null}
                  <span className="flex-1">{item.label}</span>
                  {item.status && <span className={`size-2 shrink-0 rounded-full ${STATUS_DOT_CLASS[item.status]}`} />}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Shared internal sidebar navigation for entity-detail shells (Event, Team, Regional,
 * Edition, Seedbed, Course, Group, Student profile) — replaces the old horizontal
 * EntityTabBar (gestion-entity-shell-sidebar-nav).
 */
export function EntityDetailNav<K extends string>({ groups, activeKey, onChange, tip }: EntityDetailNavProps<K>) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const activeLabel = groups.flatMap((g) => g.items).find((item) => item.key === activeKey)?.label ?? '';

  function select(key: K) {
    onChange(key);
    setMobileOpen(false);
  }

  return (
    <>
      <nav className="hidden w-60 shrink-0 space-y-3 lg:sticky lg:top-6 lg:block lg:self-start">
        <div className="rounded-2xl border border-gray-200 bg-white p-2 dark:border-gray-800 dark:bg-white/[0.03]">
          <NavList groups={groups} activeKey={activeKey} onSelect={select} />
        </div>
        {tip ? (
          <Card className="rounded-2xl border-brand-100 bg-brand-25 p-3 text-xs leading-snug text-brand-700 dark:border-brand-500/20 dark:bg-brand-500/[0.06] dark:text-brand-300">
            {tip}
          </Card>
        ) : null}
      </nav>

      <div className="mb-4 lg:hidden">
        <button
          type="button"
          onClick={() => setMobileOpen(true)}
          className="flex w-full items-center justify-between gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 dark:border-gray-800 dark:bg-white/[0.03] dark:text-gray-300"
        >
          <span>Secciones · {activeLabel}</span>
          <MenuIcon />
        </button>
      </div>

      {mobileOpen && (
        <div className="fixed inset-0 z-40 bg-gray-900/50 lg:hidden" onClick={() => setMobileOpen(false)} />
      )}
      <div
        className={`fixed inset-y-0 right-0 z-50 w-72 max-w-[85vw] overflow-y-auto bg-white p-4 shadow-xl transition-transform duration-300 ease-in-out dark:bg-gray-900 lg:hidden ${
          mobileOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="mb-3 flex items-center justify-between">
          <p className="text-sm font-semibold text-gray-800 dark:text-white/90">Secciones</p>
          <button
            type="button"
            aria-label="Cerrar"
            onClick={() => setMobileOpen(false)}
            className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 dark:hover:bg-white/[0.04]"
          >
            ✕
          </button>
        </div>
        <NavList groups={groups} activeKey={activeKey} onSelect={select} />
      </div>
    </>
  );
}

/** Two-column wrapper (nav left, content right) shared by every entity-detail shell. */
export function EntityDetailNavLayout<K extends string>({
  groups,
  activeKey,
  onChange,
  tip,
  children,
}: EntityDetailNavProps<K> & { children: ReactNode }) {
  return (
    <div className="lg:flex lg:items-start lg:gap-6">
      <EntityDetailNav groups={groups} activeKey={activeKey} onChange={onChange} tip={tip} />
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}
